import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Radar, RefreshCw, MapPin, Loader2, AlertTriangle, Settings2, Send,
  CheckCircle, XCircle, Clock, Activity, X, Search, BookOpen, Plug, ExternalLink, Newspaper, Globe2, ChevronDown,
} from 'lucide-react';
import { CommanServices } from '@/services/commanServices';
import {
  distanceLabel, warningToEvent, HAZARD_FILTERS, DEFAULT_HAZARD_IDS, eventHazards,
  hazardCoverage, HAZARD_SUPER_CONNECT, SUPER_CONNECT_TEMPLATES, suggestionPricing,
} from '../components/externalEvents';
import { toast } from 'sonner';
import ForesightDecisionQueue from '../components/ForesightDecisionQueue';
import './warningDetailLight.css';
import { readStoredUser } from '@/auth/saasSession';
import { isFeatureEnabled, LICENSE_FEATURES } from '@/auth/featureEntitlements';

const HAZARD_PREFS_KEY = 'foresightHazardTypes';
const loadHazardPrefs = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(HAZARD_PREFS_KEY) || 'null');
    if (Array.isArray(saved) && saved.length) return saved.filter((id) => HAZARD_FILTERS.some((h) => h.id === id));
  } catch (e) { /* fall back to defaults */ }
  return DEFAULT_HAZARD_IDS;
};

// Pulsating-dot colour scheme: High/Critical = red, Medium/Moderate = orange,
// Low/Mild = yellow. Used for the list dots and the map warning points.
const SEV_DOT = { High: 'bg-red-500', Medium: 'bg-orange-500', Low: 'bg-yellow-400' };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const fmtAgo = (iso, now) => {
  if (!iso) return null;
  const mins = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60000));
  return mins < 1 ? 'just now' : mins < 60 ? `${mins}m ago` : `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
};

/* ----------------------------- Feed health ----------------------------- */
// Lives in a modal opened from the watch header. Table only — polling runs by
// itself in the background; the watch refresh is the manual lever.
const FeedHealthModal = ({ onClose }) => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await CommanServices.getEarlyWarningStatus();
      if (res?.status) setData(res.data);
    } catch (e) { /* shows on next tick */ }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    window.addEventListener('foresightRefresh', load);
    return () => { clearInterval(interval); window.removeEventListener('foresightRefresh', load); };
  }, [load]);

  const feeds = (data?.feeds || []).filter((f) => f.reporting);

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4" data-testid="feed-health-modal">
      <div className="bg-white rounded-xl shadow-2xl w-[680px] max-h-[85vh] flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Activity className="h-4 w-4 text-indigo-600" /> Feed Health
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              {data ? `Auto-poll every ${Math.round((data.poll_interval_seconds || 900) / 60)} min · AOI ${data.aoi_radius_km} km around ${data.operations_area?.country_name || 'operations area'}` : 'Polling status per connection'}
            </p>
          </div>
          <button onClick={onClose} data-testid="feed-health-close"><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {!data ? (
            <div className="text-center py-10 text-sm text-gray-400">Loading feed status…</div>
          ) : (
            <table className="w-full text-sm" data-testid="feed-health-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-gray-400 bg-gray-50/70 border-b">
                  <th className="text-left font-bold px-4 py-2.5">Feed</th>
                  <th className="text-left font-bold px-3 py-2.5">Last Poll</th>
                  <th className="text-left font-bold px-3 py-2.5">Status</th>
                  <th className="text-center font-bold px-3 py-2.5">Events</th>
                  <th className="text-center font-bold px-3 py-2.5">In AOI</th>
                  <th className="text-center font-bold px-3 py-2.5">Warnings Raised</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {feeds.map((f) => {
                  const lp = f.last_poll;
                  return (
                    <tr key={f.id} className="hover:bg-slate-50/60" data-testid={`feed-health-${f.id}`}>
                      <td className="px-4 py-2.5 font-semibold text-gray-900">{f.name}</td>
                      <td className="px-3 py-2.5 text-gray-500 text-xs">{fmt(lp?.at)}</td>
                      <td className="px-3 py-2.5">
                        {!lp ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold border border-amber-200"><Clock className="w-3 h-3" /> Pending</span>
                        ) : lp.ok ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold border border-emerald-200"><CheckCircle className="w-3 h-3" /> OK</span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold border border-red-200" title={lp.error || ''}><XCircle className="w-3 h-3" /> Failed</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-center font-bold text-gray-700">{lp?.events ?? '—'}</td>
                      <td className="px-3 py-2.5 text-center font-bold text-indigo-700">{lp?.in_aoi ?? '—'}</td>
                      <td className="px-3 py-2.5 text-center font-bold text-rose-600">{lp?.warnings_created ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ------------------------ Hazard dictionary modal ------------------------ */
// The full hazard dictionary — the user picks which categories appear as
// filter chips on the watch. Stored per browser (localStorage).
const HazardDictionaryModal = ({ enabled, onChange, onClose, onManage }) => {
  const toggle = (id) => onChange(enabled.includes(id) ? enabled.filter((x) => x !== id) : [...enabled, id]);
  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4" data-testid="hazard-dictionary-modal">
      <div className="bg-white rounded-xl shadow-2xl w-[560px] max-h-[85vh] flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-indigo-600" /> Hazard Dictionary
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Choose the hazard types you care about — they appear as filter chips on the watch.
            </p>
          </div>
          <button onClick={onClose} data-testid="hazard-dictionary-close"><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 grid grid-cols-2 gap-2">
          {HAZARD_FILTERS.map((h) => {
            const on = enabled.includes(h.id);
            return (
              <div
                key={h.id}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${on ? 'border-indigo-300 bg-indigo-50 text-indigo-800' : 'border-gray-200 bg-white text-gray-600'}`}
              >
                <button type="button" onClick={() => toggle(h.id)} data-testid={`hazard-dict-${h.id}`}
                  className="flex items-center gap-2 flex-1 text-left min-w-0">
                  <span className="text-base">{h.icon}</span>
                  <span className="flex-1 font-semibold truncate">{h.label}</span>
                  <span className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${on ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'}`}>
                    {on && <CheckCircle className="h-3 w-3 text-white" />}
                  </span>
                </button>
                {onManage && (
                  <button
                    type="button"
                    onClick={() => onManage(h)}
                    data-testid={`hazard-dict-manage-${h.id}`}
                    title="Manage this hazard — coverage, scope, radius, APIs"
                    className="p-1 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-100 flex-shrink-0"
                  >
                    <Settings2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-between text-[11px] text-gray-400">
          <span>{enabled.length} of {HAZARD_FILTERS.length} types visible</span>
          <div className="flex gap-3">
            <button className="font-bold text-indigo-600" onClick={() => onChange(HAZARD_FILTERS.map((h) => h.id))}>Select all</button>
            <button className="font-bold text-gray-500" onClick={() => onChange(DEFAULT_HAZARD_IDS)}>Reset to default</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ------------------------- Zero-count explainer ------------------------- */
// Clicking a hazard chip with 0 events explains WHY: either no connected feed
// covers this hazard (suggest APIs), or the feeds are connected but quiet.
export const ZeroHazardModal = ({ hazard, connections, onClose, onConnected }) => {
  const navigate = useNavigate();
  const covering = hazardCoverage(hazard, connections);
  const [connecting, setConnecting] = useState(null);
  const [scResults, setScResults] = useState({}); // templateKey -> { ok, detail }
  const [probes, setProbes] = useState({});       // url -> { ok, status }
  const [aiState, setAiState] = useState({ loading: false, error: null, items: null });

  // Live reachability for the curated suggestions — real "online now" status.
  useEffect(() => {
    (async () => {
      try {
        const res = await CommanServices.probeSuggestionUrls(hazard.suggestions.map((s) => s.url));
        if (res?.status) setProbes(res.data?.results || {});
      } catch (e) { /* badges just stay neutral */ }
    })();
  }, [hazard]);

  // Watch scope for this hazard (AOI vs Global) plus the SaaS-managed,
  // institution-wide Operations Area shown as read-only context.
  const [scope, setScope] = useState('aoi');
  const [scopeSaving, setScopeSaving] = useState(false);
  const [area, setArea] = useState(null);
  useEffect(() => {
    let active = true;
    const loadScope = async () => {
      try {
        const scopesRes = await CommanServices.getHazardScopes();
        if (active && scopesRes?.status) setScope(scopesRes.data?.scopes?.[hazard.id]?.scope === 'global' ? 'global' : 'aoi');
      } catch (e) { /* scope falls back to AOI */ }
    };
    const loadArea = async () => {
      try {
        const areaRes = await CommanServices.getOperationsArea();
        if (active) setArea(areaRes?.status && areaRes.data ? areaRes.data : null);
      } catch (e) {
        if (active) setArea(null);
      }
    };
    loadScope();
    loadArea();
    window.addEventListener('focus', loadArea);
    window.addEventListener('operations-area-changed', loadArea);
    window.addEventListener('disasterms:saas-institutions-changed', loadArea);
    return () => {
      active = false;
      window.removeEventListener('focus', loadArea);
      window.removeEventListener('operations-area-changed', loadArea);
      window.removeEventListener('disasterms:saas-institutions-changed', loadArea);
    };
  }, [hazard]);

  const saveScope = async (next) => {
    setScopeSaving(true);
    try {
      const res = await CommanServices.setHazardScope({ hazard_id: hazard.id, scope: next, keywords: hazard.keywords });
      if (!res?.status) { toast.error(res?.message || 'Failed to save scope'); return; }
      setScope(next);
      toast.success(next === 'global'
        ? `${hazard.label}: watching GLOBALLY — High/Medium events of this type will warn from anywhere`
        : `${hazard.label}: back to the Operations Area radius`);
    } finally {
      setScopeSaving(false);
    }
  };

  const discover = async () => {
    setAiState({ loading: true, error: null, items: null });
    const res = await CommanServices.discoverApisForHazard(hazard.label);
    if (res?.status) setAiState({ loading: false, error: null, items: res.data?.suggestions || [] });
    else setAiState({ loading: false, error: res?.message || 'AI discovery failed', items: null });
  };

  // SuperConnect — registers the connection, then VERIFIES it for real:
  // pings the URL and (for event feeds) fetches live events, reporting the
  // actual outcome instead of a blind "connected".
  const superTemplates = (HAZARD_SUPER_CONNECT[hazard.id] || [])
    .map((key) => ({ key, tpl: SUPER_CONNECT_TEMPLATES[key] }))
    .filter(({ tpl }) => tpl && !connections.some((c) => (c.name || '').toLowerCase() === tpl.name.toLowerCase()));

  const superConnect = async ({ key, tpl }) => {
    if (connecting) return;
    setConnecting(key);
    try {
      const res = await CommanServices.addApiConnection(tpl);
      if (!res?.status) {
        setScResults((p) => ({ ...p, [key]: { ok: false, detail: res?.message || 'Could not create the connection.' } }));
        toast.error(res?.message || 'SuperConnect failed');
        return;
      }
      const conn = res.data;
      // Verification pass — the user gets the REAL result.
      let detail = 'Connected.';
      let ok = true;
      try {
        const t = await CommanServices.testApiConnection(conn.id);
        const tr = t?.status ? t.data : null;
        // Strict verification: only 2xx/3xx counts as working — a 4xx means the
        // URL/params/key are wrong, and the user deserves to know that.
        if (tr?.ok && tr.status_code && tr.status_code < 400) {
          detail = `Verified — endpoint reachable (HTTP ${tr.status_code}, ${tr.latency_ms} ms).`;
          if ((tpl.purposes || []).includes('incident_reporting')) {
            const f = await CommanServices.fetchApiConnectionEvents(conn.id);
            if (f?.status) detail = `Verified & working — ${f.data?.count ?? 0} live event(s) fetched just now.`;
            else detail = `Reachable, but event fetch failed: ${(f?.message || '').slice(0, 90)}. It may be rate-limited — retry in a minute.`;
          }
        } else {
          ok = false;
          detail = `Connected, but the endpoint test failed${tr?.error ? `: ${tr.error.slice(0, 80)}` : tr?.status_code ? ` (HTTP ${tr.status_code})` : ''}. Check the URL or credentials on the API Connections page.`;
        }
      } catch (e) {
        detail = 'Connected; verification could not complete — use Test on the API Connections page.';
      }
      setScResults((p) => ({ ...p, [key]: { ok, detail } }));
      (ok ? toast.success : toast.warning)(`"${tpl.name}": ${detail}`);
      onConnected && (await onConnected());
    } finally {
      setConnecting(null);
    }
  };
  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4" data-testid="zero-hazard-modal">
      <div className="bg-white rounded-xl shadow-2xl w-[480px] p-5 space-y-3.5">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">{hazard.icon} {hazard.label} — no events</h3>
          <button onClick={onClose} data-testid="zero-hazard-close"><X className="w-4 h-4 text-gray-400" /></button>
        </div>

        {covering.length > 0 ? (
          <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2.5 text-[12px] text-emerald-800 leading-relaxed" data-testid="zero-hazard-covered">
            <span className="font-bold">Good news:</span> your connected feed{covering.length > 1 ? 's' : ''}{' '}
            <span className="font-semibold">{covering.map((c) => c.name).join(', ')}</span> cover{covering.length > 1 ? '' : 's'} this hazard,
            but nothing has been reported inside your Operations Area right now.
          </div>
        ) : (
          <div className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2.5 text-[12px] text-amber-800 leading-relaxed" data-testid="zero-hazard-uncovered">
            <span className="font-bold">Coverage gap:</span> none of your API connections watches this hazard type —
            events of this kind can't be detected until a matching feed is connected.
          </div>
        )}

        {/* Per-hazard scope remains editable; institution area is SaaS-managed. */}
        <div className="rounded-lg border border-gray-100 bg-slate-50/60 p-3 space-y-2.5" data-testid="zero-hazard-scope">
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1">
            <Globe2 className="h-3.5 w-3.5 text-indigo-500" /> Watch scope for {hazard.label}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" disabled={scopeSaving} onClick={() => saveScope('aoi')} data-testid="scope-aoi"
              className={`rounded-lg border px-2 py-2 text-[11px] font-bold transition-colors ${scope === 'aoi' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              Operations Area{area ? ` (${area.radius_km} km)` : ''}
            </button>
            <button type="button" disabled={scopeSaving} onClick={() => saveScope('global')} data-testid="scope-global"
              className={`rounded-lg border px-2 py-2 text-[11px] font-bold transition-colors ${scope === 'global' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              🌍 Global — warn from anywhere
            </button>
          </div>
          <div className="rounded-lg border border-indigo-100 bg-white px-3 py-2.5" data-testid="zero-hazard-operations-area">
            <div className="flex items-start justify-between gap-3">
              <div>
                <span className="block text-[10px] font-bold uppercase tracking-wider text-gray-400">Institution Operations Area</span>
                <strong className="mt-0.5 block text-[12px] text-gray-800" data-testid="zero-hazard-area-country">
                  {area?.country_name || area?.country_iso2 || 'Unavailable'}
                </strong>
              </div>
              <span className="text-[11px] font-bold text-indigo-600" data-testid="zero-hazard-area-radius">
                {area?.radius_km != null ? `${Number(area.radius_km).toLocaleString()} km radius` : '—'}
              </span>
            </div>
            <p className="mt-1.5 text-[10px] leading-relaxed text-gray-400">
              Managed by the SaaS account and read-only here. Choose AOI or Global above only for this hazard feed.
            </p>
          </div>
        </div>

        {superTemplates.length > 0 && (
          <div data-testid="zero-hazard-superconnect">
            <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-500 mb-1.5">
              ⚡ SuperConnect — one click, connected AND verified
            </p>
            <div className="space-y-1.5">
              {superTemplates.map((s) => (
                <div key={s.key}>
                  <button
                    type="button"
                    onClick={() => superConnect(s)}
                    disabled={!!connecting}
                    data-testid={`superconnect-${s.key}`}
                    className="w-full flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-indigo-800 text-[12px] font-semibold px-3 py-2 disabled:opacity-60"
                  >
                    {connecting === s.key ? <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" /> : <Plug className="h-3.5 w-3.5 flex-shrink-0" />}
                    <span className="flex-1 text-left">{s.tpl.name}</span>
                    <span className="text-[10px] font-bold text-indigo-500">{connecting === s.key ? 'Verifying…' : 'Connect now'}</span>
                  </button>
                  {scResults[s.key] && (
                    <p className={`mt-1 text-[11px] px-1 ${scResults[s.key].ok ? 'text-emerald-700' : 'text-amber-700'}`} data-testid={`superconnect-result-${s.key}`}>
                      {scResults[s.key].ok ? '✅' : '⚠️'} {scResults[s.key].detail}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">
            {covering.length > 0 ? 'APIs that could broaden coverage' : 'Suggested APIs for this hazard'}
            <span className="normal-case font-normal text-gray-400"> · status checked live</span>
          </p>
          <div className="space-y-1.5">
            {hazard.suggestions.map((s) => {
              const pricing = suggestionPricing(s);
              const probe = probes[s.url];
              return (
                <div key={s.url} className="flex items-center gap-2">
                  <a href={s.url} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 text-[12px] text-indigo-600 hover:text-indigo-800 hover:underline truncate">
                    <ExternalLink className="h-3 w-3 flex-shrink-0" /> {s.name}
                  </a>
                  <span className={`text-[9px] font-bold rounded-full border px-1.5 py-0.5 flex-shrink-0 ${pricing.cls}`}>{pricing.label}</span>
                  {probe && (
                    <span className={`text-[9px] font-bold rounded-full border px-1.5 py-0.5 flex-shrink-0 ${probe.ok ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-600 border-rose-200'}`}
                      title={probe.ok ? `Reachable (HTTP ${probe.status})` : 'Did not respond to a live check just now'}>
                      {probe.ok ? '● online' : '○ unreachable'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* AI discovery — live, LLM-backed; honest about availability */}
        <div data-testid="zero-hazard-ai">
          {aiState.items === null && !aiState.loading && !aiState.error && (
            <button
              type="button"
              onClick={discover}
              data-testid="ai-discover-btn"
              className="w-full flex items-center justify-center gap-1.5 rounded-md border border-violet-200 bg-violet-50 hover:bg-violet-100 text-violet-700 text-[12px] font-bold py-2"
            >
              🔎 Discover more APIs with AI
            </button>
          )}
          {aiState.loading && (
            <p className="text-[11px] text-violet-600 flex items-center gap-1.5 justify-center py-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching for {hazard.label.toLowerCase()} data sources…
            </p>
          )}
          {aiState.error && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2.5 py-2" data-testid="ai-discover-error">
              {aiState.error}
            </p>
          )}
          {Array.isArray(aiState.items) && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-violet-500">AI-discovered — verify before relying on them</p>
              {aiState.items.length === 0 && <p className="text-[11px] text-gray-400">The AI found nothing beyond the suggestions above.</p>}
              {aiState.items.map((s) => (
                <div key={s.url} className="flex items-start gap-2">
                  <a href={s.url} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 text-[12px] text-indigo-600 hover:text-indigo-800 hover:underline">
                    <ExternalLink className="h-3 w-3 flex-shrink-0" /> {s.name}
                  </a>
                  <span className={`text-[9px] font-bold rounded-full border px-1.5 py-0.5 flex-shrink-0 ${s.pricing === 'free' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : s.pricing === 'registration' ? 'bg-sky-50 text-sky-700 border-sky-200' : 'bg-orange-50 text-orange-700 border-orange-200'}`}>
                    {s.pricing || 'unknown'}
                  </span>
                  {s.note && <span className="text-[10px] text-gray-400 leading-snug">{s.note}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={() => { onClose(); navigate('/api-connections'); }}
          data-testid="zero-hazard-add-connection"
          className="w-full flex items-center justify-center gap-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold py-2"
        >
          <Plug className="h-4 w-4" /> Add an API connection manually
        </button>
      </div>
    </div>,
    document.body
  );
};

/* ------------------------- Early Warning Watch ------------------------- */
// ONE list: live feed events (re-queried on every open/refresh, they drop off
// naturally as the feeds roll over) MERGED with the persistent alarm log —
// warnings the 15-min poller saved. A warned event shows a pulsating dot and
// stays listed even after it leaves the live feeds.
const sevRank = { High: 0, Medium: 1, Low: 2 };
// Cornerstone rule: events INSIDE the Operations Area country always come
// first, before anything external — then by AOI, distance, severity.
const relevanceSort = (a, b) => {
  const bucket = (e) => (e.internal ? 0 : e.in_aoi ? 1 : e.distance_km != null ? 2 : 3);
  return (bucket(a) - bucket(b))
    || ((a.distance_km ?? 1e9) - (b.distance_km ?? 1e9))
    || ((sevRank[a.severity] ?? 3) - (sevRank[b.severity] ?? 3));
};

/* ---------------------------- Zone risk strip ---------------------------- */
// WHERE is the risk: worst active impact level per operational zone, live.
const ZONE_LEVEL_STYLE = {
  Critical: 'border-red-300 bg-red-50 text-red-700',
  High: 'border-rose-200 bg-rose-50 text-rose-700',
  Medium: 'border-orange-200 bg-orange-50 text-orange-700',
  Low: 'border-yellow-200 bg-yellow-50 text-yellow-700',
  Informational: 'border-gray-200 bg-gray-50 text-gray-500',
};

const ZoneRiskStrip = () => {
  const [zones, setZones] = useState([]);
  const load = useCallback(async () => {
    try {
      const res = await CommanServices.getZoneRisk();
      const data = res?.data || res;
      if (data?.zones) setZones(data.zones);
    } catch (e) { /* strip just stays hidden */ }
  }, []);
  useEffect(() => {
    load();
    const heartbeat = setInterval(load, 60000);
    window.addEventListener('earlyWarningsChanged', load);
    return () => { clearInterval(heartbeat); window.removeEventListener('earlyWarningsChanged', load); };
  }, [load]);
  if (!zones.length) return null;
  return (
    <div className="fs-sec-card mb-5" data-testid="zone-risk-strip">
      <div className="fs-sec-head px-5 py-3 flex items-center gap-3">
        <div className="fs-sec-icon"><MapPin className="h-4 w-4" /></div>
        <div>
          <h3 className="text-sm font-bold leading-tight">Risk by area</h3>
          <p className="text-[11px] fs-sec-sub">worst active IMPACT level per zone — external events that can't reach us stay grey</p>
        </div>
      </div>
      <div className="p-4 flex flex-wrap gap-2">
        {zones.map((z) => (
          <div key={z.zone}
            className={`rounded-lg border px-3 py-1.5 ${ZONE_LEVEL_STYLE[z.max_level] || ZONE_LEVEL_STYLE.Informational}`}
            data-testid={`zone-risk-${z.zone}`}
            title={(z.top || []).map((t) => `${t.score ?? '—'} · ${t.title}`).join('\n')}>
            <p className="text-[12px] font-bold">{z.zone}</p>
            <p className="text-[10px] font-semibold opacity-80">
              {['Critical', 'High', 'Medium', 'Low', 'Informational']
                .filter((l) => z.counts[l]).map((l) => `${z.counts[l]} ${l}`).join(' · ')}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const EarlyWarningWatch = ({ onPreview, apiConnectionsEnabled, iotEnabled }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [feedHealthOpen, setFeedHealthOpen] = useState(false);
  const [hazardFilter, setHazardFilter] = useState([]); // empty = all hazards
  const [severityFilter, setSeverityFilter] = useState([]); // empty = all severities
  const [query, setQuery] = useState('');
  const [hazardPrefs, setHazardPrefs] = useState(loadHazardPrefs);
  const [showAllIntel, setShowAllIntel] = useState(false);
  const [intelQuery, setIntelQuery] = useState('');
  const [intelHazardFilter, setIntelHazardFilter] = useState([]);
  const [ewwCollapsed, setEwwCollapsed] = useState(false);
  const [intelCollapsed, setIntelCollapsed] = useState(false);
  const [predictionCollapsed, setPredictionCollapsed] = useState(true);
  const [dictionaryOpen, setDictionaryOpen] = useState(false);
  const [zeroHazard, setZeroHazard] = useState(null);
  const [connections, setConnections] = useState([]);
  const [lastPollAt, setLastPollAt] = useState(null);

  const saveHazardPrefs = (ids) => {
    setHazardPrefs(ids);
    try { localStorage.setItem(HAZARD_PREFS_KEY, JSON.stringify(ids)); } catch (e) { /* prefs just won't persist */ }
    setHazardFilter((prev) => prev.filter((id) => ids.includes(id)));
  };

  const load = useCallback(async (refresh = false, silent = false) => {
    if (!silent) { refresh ? setRefreshing(true) : setLoading(true); }
    try {
      // A manual refresh ALSO runs a poll cycle so the alarm log and the Feed
      // Health "last poll" move in lockstep with the watch.
      if (refresh && apiConnectionsEnabled) {
        try { await CommanServices.pollExternalFeedsNow(); } catch (e) { /* poll is best-effort */ }
      }
      const [watchRes, warnRes, connRes, statusRes] = await Promise.all([
        apiConnectionsEnabled ? CommanServices.getExternalEventsWatch(refresh) : Promise.resolve(null),
        CommanServices.getEarlyWarnings(300),
        apiConnectionsEnabled ? CommanServices.getApiConnections() : Promise.resolve(null),
        apiConnectionsEnabled ? CommanServices.getEarlyWarningStatus() : Promise.resolve(null),
      ]);
      if (watchRes?.status) setData(watchRes.data);
      if (!apiConnectionsEnabled) setData({ events: [], sources: [] });
      if (warnRes?.status) setWarnings(warnRes.data?.warnings || []);
      if (connRes?.status) setConnections(Array.isArray(connRes.data) ? connRes.data : []);
      if (statusRes?.status) {
        const polls = (statusRes.data?.feeds || []).map((f) => f.last_poll?.at).filter(Boolean).sort();
        setLastPollAt(polls.length ? polls[polls.length - 1] : null);
      }
      window.dispatchEvent(new Event('foresightRefresh'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [apiConnectionsEnabled]);

  useEffect(() => {
    load();
    // LIVE: silent re-sync — a socket push (new warning raised/dispatched),
    // a 60s heartbeat, or the operator returning to the tab all refresh the
    // watch without any spinner flashing.
    const silentSync = () => load(false, true);
    const heartbeat = setInterval(silentSync, 60000);
    window.addEventListener('earlyWarningsChanged', silentSync);
    window.addEventListener('focus', silentSync);
    return () => {
      clearInterval(heartbeat);
      window.removeEventListener('earlyWarningsChanged', silentSync);
      window.removeEventListener('focus', silentSync);
    };
  }, [load]);

  // Tick every 30s so "Last poll Xm ago" stays honest without refetching.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const tick = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(tick);
  }, []);

  // EARLY WARNING WATCH takes only REAL events (structured feeds with clear
  // location/severity/type): persistent warnings + live event-kind events,
  // deduped by external_id. News/social/papers are split out into the Intel
  // Watch below — they are signals, never warnings.
  const merged = useMemo(() => {
    const byId = new Map();
    warnings.forEach((w) => byId.set(w.external_id, { ...warningToEvent(w), isWarning: true }));
    (data?.events || []).filter((ev) => ev.kind !== 'intel').forEach((ev) => {
      const key = ev.external_id;
      const warned = byId.get(key);
      byId.set(key, warned ? { ...ev, warning_id: warned.warning_id, dispatched: warned.dispatched, isWarning: true } : ev);
    });
    return [...byId.values()].map((e) => ({ ...e, hazards: eventHazards(e) })).sort(relevanceSort);
  }, [data, warnings]);

  // LIVE: newly arrived items animate in with a short glow; fresh WARNINGS
  // additionally raise a toast so the operator never misses one.
  // The announced set only ever GROWS: live-watch events naturally drop in
  // and out of the merged set between fetches, and diffing against the last
  // snapshot re-announced old items forever. An item is "new" the first time
  // this session sees it — never again. Toasts additionally require the
  // warning to be genuinely recent, and carry a stable id so a double fire
  // can only replace itself, never stack.
  const TOAST_RECENT_MS = 60 * 60 * 1000; // announce warnings younger than 1h
  const seenIdsRef = useRef(null);
  const [newIds, setNewIds] = useState(() => new Set());
  useEffect(() => {
    if (!merged.length) return undefined;
    const ids = merged.map((e) => e.external_id).filter(Boolean);
    if (seenIdsRef.current === null) {
      // first data of the session: seed silently, nothing is "new"
      seenIdsRef.current = new Set(ids);
      return undefined;
    }
    const fresh = ids.filter((id) => !seenIdsRef.current.has(id));
    if (!fresh.length) return undefined;
    fresh.forEach((id) => seenIdsRef.current.add(id));
    setNewIds(new Set(fresh));
    merged
      .filter((e) => fresh.includes(e.external_id) && e.isWarning
        && e.occurred_at && (Date.now() - new Date(e.occurred_at).getTime()) < TOAST_RECENT_MS)
      .slice(0, 3)
      .forEach((w) => toast.warning(`New early warning: ${w.title}`, { id: `ew-${w.external_id}`, duration: 8000 }));
    const clear = setTimeout(() => setNewIds(new Set()), 4000);
    return () => clearTimeout(clear);
  }, [merged]);

  // Intel Watch: news, breaking news, social media, papers — internal AND
  // external coverage, newest first, with hazard classification like the EWW.
  const intelEvents = useMemo(
    () => (data?.events || [])
      .filter((ev) => ev.kind === 'intel')
      .map((e) => ({ ...e, hazards: eventHazards(e) }))
      .sort((a, b) => new Date(b.occurred_at || 0) - new Date(a.occurred_at || 0)),
    [data]
  );
  const intelHazardCounts = useMemo(() => {
    const counts = {};
    intelEvents.forEach((e) => e.hazards.forEach((h) => { counts[h] = (counts[h] || 0) + 1; }));
    return counts;
  }, [intelEvents]);
  // Failed-feed notices are scoped per section: event feeds under the EWW,
  // news/social feeds under the Intel Watch.
  const failedEventSources = (data?.sources || []).filter((s) => !s.ok && s.kind !== 'intel');
  const failedIntelSources = (data?.sources || []).filter((s) => !s.ok && s.kind === 'intel');

  // Hazard counts feed the filter chips; filters + search narrow the list.
  const hazardCounts = useMemo(() => {
    const counts = {};
    merged.forEach((e) => e.hazards.forEach((h) => { counts[h] = (counts[h] || 0) + 1; }));
    return counts;
  }, [merged]);

  const filtered = useMemo(() => {
    let list = merged;
    if (hazardFilter.length) list = list.filter((e) => e.hazards.some((h) => hazardFilter.includes(h)));
    if (severityFilter.length) list = list.filter((e) => severityFilter.includes(e.severity));
    const q = query.trim().toLowerCase();
    // Search covers title, brief, source AND severity ("high" finds High events)
    if (q) list = list.filter((e) => `${e.title} ${e.description} ${e.connection?.name || ''} ${e.severity || ''}`.toLowerCase().includes(q));
    return list;
  }, [merged, hazardFilter, severityFilter, query]);

  const toggleHazard = (id) => setHazardFilter((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleSeverity = (s) => setSeverityFilter((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const shown = showAll ? filtered : filtered.slice(0, 8);
  // The badge follows the FILTERED view — applying filters changes the count.
  const warningCount = filtered.filter((e) => e.isWarning).length;
  const countryName = data?.operations_area?.country_name || 'operations country';

  return (
    <>
    <div className="fs-sec-card" data-testid="early-warning-watch">
      <div className="fs-sec-head px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="fs-sec-icon"><Radar className="h-5 w-5" /></div>
          <div>
            <h3 className="text-sm font-bold leading-tight flex items-center gap-2">
              Early Warning Watch
              <span className="fs-live-dot" title="Live — updates the moment a warning is raised, plus a 60s heartbeat" />
              {warningCount > 0 && (
                <span
                  className="text-[10px] font-bold fs-count-red rounded-full px-2 py-0.5"
                  data-testid="watch-warning-count"
                  title="Saved early warnings in the current view. 'All events' also counts live feed events that never crossed the warning threshold."
                >
                  {warningCount} warnings
                </span>
              )}
            </h3>
            <p className="text-[11px] fs-sec-sub">
              {data ? `Live + alarm log · nearest first · AOI ${data.aoi_radius_km} km around ${data.operations_area?.country_name || 'operations area'}` : 'Live feeds from API Connections'}
              {lastPollAt && <span data-testid="watch-last-poll"> · Last poll {fmtAgo(lastPollAt, nowTick)}</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => load(true)} disabled={refreshing} data-testid="watch-refresh"
            className="p-1.5 rounded-md fs-sec-btn disabled:opacity-60" title="Refresh feeds now">
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setDictionaryOpen(true)} data-testid="watch-dictionary"
            className="p-1.5 rounded-md fs-sec-btn" title="Hazard dictionary — choose the types you're interested in">
            <BookOpen className="h-4 w-4" />
          </button>
          {apiConnectionsEnabled && (
            <button onClick={() => setFeedHealthOpen(true)} data-testid="watch-feed-health"
              className="p-1.5 rounded-md fs-sec-btn" title="Feed health">
              <Activity className="h-4 w-4" />
            </button>
          )}
          {apiConnectionsEnabled && (
            <button onClick={() => navigate('/api-connections')} data-testid="watch-manage"
              className="p-1.5 rounded-md fs-sec-btn" title="Manage API connections">
              <Settings2 className="h-4 w-4" />
            </button>
          )}
          <button onClick={() => setEwwCollapsed((v) => !v)} data-testid="watch-collapse"
            className="p-1.5 rounded-md fs-sec-btn" title={ewwCollapsed ? 'Expand' : 'Collapse'}>
            <ChevronDown className={`h-4 w-4 transition-transform ${ewwCollapsed ? '' : 'rotate-180'}`} />
          </button>
        </div>
      </div>

      {!ewwCollapsed && (
      <div className="p-4">
        {/* Hazard filters + search */}
        <div className="mb-3 space-y-2" data-testid="watch-filters">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search events, warnings, sources…"
              data-testid="watch-search"
              className="w-full text-sm border border-gray-200 rounded-lg bg-slate-50/60 pl-8 pr-3 py-1.5 focus:outline-none focus:border-indigo-300 focus:bg-white"
            />
          </div>
          {/* Severity filter */}
          <div className="flex flex-wrap items-center gap-1.5" data-testid="severity-filters">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mr-0.5">Severity</span>
            {[{ s: 'High', cls: 'bg-red-500' }, { s: 'Medium', cls: 'bg-orange-500' }, { s: 'Low', cls: 'bg-yellow-400' }].map(({ s, cls }) => {
              const active = severityFilter.includes(s);
              return (
                <button key={s} type="button" onClick={() => toggleSeverity(s)} data-testid={`severity-filter-${s.toLowerCase()}`}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                  <span className={`h-2 w-2 rounded-full ${cls}`} /> {s} ({merged.filter((e) => e.severity === s).length})
                </button>
              );
            })}
          </div>

          {/* Hazard chips — every enabled type shows, 0-count included; a 0
              chip explains why instead of filtering */}
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setHazardFilter([])}
              data-testid="hazard-filter-all"
              className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${hazardFilter.length === 0 ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
              title="Everything in view: saved warnings + live feed events that didn't cross the warning threshold"
            >
              All events ({merged.length})
            </button>
            {HAZARD_FILTERS.filter((h) => hazardPrefs.includes(h.id)).map((h) => {
              const count = hazardCounts[h.id] || 0;
              const active = hazardFilter.includes(h.id);
              return (
                <button
                  key={h.id}
                  type="button"
                  onClick={() => (count > 0
                    ? toggleHazard(h.id)
                    : apiConnectionsEnabled ? setZeroHazard(h) : undefined)}
                  data-testid={`hazard-filter-${h.id}`}
                  title={count === 0 ? 'No events — click to see why and which APIs could help' : undefined}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${active ? 'bg-indigo-600 text-white border-indigo-600' : count === 0 ? 'bg-white text-gray-400 border-dashed border-gray-300 hover:border-indigo-300 hover:text-indigo-500' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                >
                  {h.icon} {h.label} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8 text-sm text-gray-400" data-testid="watch-loading">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Contacting external feeds…
          </div>
        ) : shown.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-400">
            {merged.length === 0 ? 'No external events or warnings right now.' : 'Nothing matches the current filters.'}
          </div>
        ) : (
          <div className="space-y-2" data-testid="watch-events-list">
            {shown.map((ev) => (
              <div key={ev.external_id}
                role="button"
                onClick={() => onPreview(ev)}
                className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${newIds.has(ev.external_id) ? 'fs-row-new ' : ''}${ev.isWarning ? 'border-red-200 bg-red-50/40 hover:bg-red-50' : ev.in_aoi ? 'border-indigo-200 bg-indigo-50/50 hover:bg-indigo-50' : 'border-gray-100 bg-slate-50/40 hover:bg-slate-50'}`}
                data-testid={`watch-event-${ev.external_id}`}>
                {ev.isWarning ? (
                  <span className="relative mt-1.5 flex h-2.5 w-2.5 flex-shrink-0" title={`Early warning · ${ev.severity}`}>
                    <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping ${SEV_DOT[ev.severity] || 'bg-orange-500'}`} />
                    <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${SEV_DOT[ev.severity] || 'bg-orange-500'}`} />
                  </span>
                ) : (
                  <span className={`mt-1.5 h-2.5 w-2.5 rounded-full flex-shrink-0 ${SEV_DOT[ev.severity] || 'bg-gray-300'}`} title={ev.severity} />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-gray-800 leading-snug truncate" title={ev.title}>
                    {ev.internal && (
                      <span
                        data-testid={`watch-internal-${ev.external_id}`}
                        className="mr-1.5 inline-flex items-center rounded bg-red-600 text-white text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 align-middle"
                        title={`Inside ${countryName} — highest priority`}
                      >
                        Internal
                      </span>
                    )}
                    {ev.title}
                  </p>
                  <p className="text-[11px] text-gray-500 flex items-center gap-1 flex-wrap">
                    <span className="font-medium text-gray-600">{ev.connection?.name}</span>
                    {ev.gps && (
                      <span className={`inline-flex items-center gap-0.5 ${ev.in_aoi ? 'font-bold text-indigo-600' : ''}`}>
                        · <MapPin className="h-3 w-3" /> {distanceLabel(ev)}
                      </span>
                    )}
                    {ev.occurred_at && <span>· {new Date(ev.occurred_at).toLocaleString()}</span>}
                  </p>
                </div>
                {ev.risk?.score !== undefined && ev.risk?.score !== null && (
                  <span
                    data-testid={`risk-chip-${ev.external_id}`}
                    className={`flex items-center gap-1 rounded-full border text-[10px] font-bold px-2 py-1 flex-shrink-0 ${
                      ev.risk.level === 'Critical' ? 'bg-red-100 text-red-700 border-red-200'
                      : ev.risk.level === 'High' ? 'bg-rose-50 text-rose-700 border-rose-200'
                      : ev.risk.level === 'Medium' ? 'bg-orange-50 text-orange-700 border-orange-200'
                      : 'bg-gray-50 text-gray-600 border-gray-200'} ${ev.risk.stale ? 'animate-pulse' : ''}`}
                    title={`Risk ${ev.risk.score}/100 · ${ev.risk.level}${ev.risk.stale ? ' · new evidence — reassess' : ''}`}
                  >
                    {ev.risk.score} · {ev.risk.level}
                  </span>
                )}
                {(ev.corroboration?.independent_count || 0) > 0 && (
                  <span
                    data-testid={`corroboration-badge-${ev.external_id}`}
                    className="flex items-center gap-1 rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-[10px] font-bold px-2 py-1 flex-shrink-0"
                    title={`${ev.corroboration.independent_count} independent evidence origin(s)`}
                  >
                    <Plug className="h-3 w-3" /> {ev.corroboration.independent_count} evidence origins
                  </span>
                )}
                {ev.dispatched && (
                  <span
                    data-testid={`watch-warned-${ev.external_id}`}
                    className="flex items-center gap-1 rounded-full bg-red-50 border border-red-200 text-red-700 text-[10px] font-bold px-2 py-1 flex-shrink-0"
                    title="A warning for this event was dispatched to all app users"
                  >
                    ⚠ Warned
                  </span>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); onPreview(ev); }}
                  data-testid={`watch-dispatch-${ev.external_id}`}
                  className="flex items-center gap-1 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-bold px-2 py-1.5 flex-shrink-0"
                  title="Preview & send to dispatch"
                >
                  <Send className="h-3 w-3" /> Dispatch
                </button>
              </div>
            ))}
            {filtered.length > 8 && (
              <button type="button" onClick={() => setShowAll((v) => !v)} data-testid="watch-see-more"
                className="w-full text-center text-[11px] font-bold text-indigo-600 hover:text-indigo-800 py-1">
                {showAll ? 'Show less' : `See more (${filtered.length - 8} more)`}
              </button>
            )}
          </div>
        )}

        {failedEventSources.length > 0 && (
          <p className="mt-3 text-[10px] text-amber-600 flex items-center gap-1" data-testid="eww-failed-sources">
            <AlertTriangle className="h-3 w-3 flex-shrink-0" />
            {failedEventSources.length} event feed{failedEventSources.length === 1 ? '' : 's'} unavailable: {failedEventSources.map((s) => s.name).join(', ')}
          </p>
        )}
      </div>
      )}

      {apiConnectionsEnabled && feedHealthOpen && <FeedHealthModal onClose={() => setFeedHealthOpen(false)} />}
      {dictionaryOpen && (
        <HazardDictionaryModal
          enabled={hazardPrefs}
          onChange={saveHazardPrefs}
          onClose={() => setDictionaryOpen(false)}
          onManage={apiConnectionsEnabled
            ? (h) => { setDictionaryOpen(false); setZeroHazard(h); }
            : null}
        />
      )}
      {apiConnectionsEnabled && zeroHazard && (
        <ZeroHazardModal
          hazard={zeroHazard}
          connections={connections}
          onClose={() => setZeroHazard(null)}
          onConnected={() => load(false)}
        />
      )}
    </div>

    {/* --------------- Intel Watch — signals, never warnings --------------- */}
    {/* Global by design: internal AND external news/social coverage; items
        mentioning the Operations Area country are tagged. Full toolbar parity
        with the Early Warning Watch. */}
    <div className="fs-sec-card mt-5" data-testid="intel-watch">
      <div className="fs-sec-head px-5 py-3.5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="fs-sec-icon sky"><Newspaper className="h-4 w-4" /></div>
          <div>
            <h3 className="text-sm font-bold leading-tight flex items-center gap-2">
              Intel Watch
              <span className="fs-live-dot" title="Live — refreshes with the watch heartbeat" />
              <span className="text-[10px] font-bold fs-count-grey rounded-full px-2 py-0.5" data-testid="intel-count">
                {intelEvents.length} signal{intelEvents.length === 1 ? '' : 's'}
              </span>
            </h3>
            <p className="text-[11px] fs-sec-sub">
              News · breaking news · social media · reports — internal &amp; worldwide, never auto-warnings
              {data && <span> · newest first · AOI {data.aoi_radius_km} km around {countryName}</span>}
              {lastPollAt && <span> · Last poll {fmtAgo(lastPollAt, nowTick)}</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => load(true)} disabled={refreshing} data-testid="intel-refresh"
            className="p-1.5 rounded-md fs-sec-btn disabled:opacity-60" title="Refresh feeds now">
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setDictionaryOpen(true)} data-testid="intel-dictionary"
            className="p-1.5 rounded-md fs-sec-btn" title="Hazard dictionary">
            <BookOpen className="h-4 w-4" />
          </button>
          {apiConnectionsEnabled && (
            <button onClick={() => setFeedHealthOpen(true)} data-testid="intel-feed-health"
              className="p-1.5 rounded-md fs-sec-btn" title="Feed health">
              <Activity className="h-4 w-4" />
            </button>
          )}
          {apiConnectionsEnabled && (
            <button onClick={() => navigate('/api-connections')} data-testid="intel-manage"
              className="p-1.5 rounded-md fs-sec-btn" title="Manage API connections">
              <Settings2 className="h-4 w-4" />
            </button>
          )}
          <button onClick={() => setIntelCollapsed((v) => !v)} data-testid="intel-collapse"
            className="p-1.5 rounded-md fs-sec-btn" title={intelCollapsed ? 'Expand' : 'Collapse'}>
            <ChevronDown className={`h-4 w-4 transition-transform ${intelCollapsed ? '' : 'rotate-180'}`} />
          </button>
        </div>
      </div>
      {!intelCollapsed && (
      <div className="p-4">
        <div className="relative mb-2">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
          <input
            value={intelQuery}
            onChange={(e) => setIntelQuery(e.target.value)}
            placeholder="Search signals…"
            data-testid="intel-search"
            className="w-full text-sm border border-gray-200 rounded-lg bg-slate-50/60 pl-8 pr-3 py-1.5 focus:outline-none focus:border-slate-300 focus:bg-white"
          />
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          <button
            type="button"
            onClick={() => setIntelHazardFilter([])}
            data-testid="intel-hazard-filter-all"
            className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${intelHazardFilter.length === 0 ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
          >
            All signals ({intelEvents.length})
          </button>
          {HAZARD_FILTERS.filter((h) => hazardPrefs.includes(h.id)).map((h) => {
            const count = intelHazardCounts[h.id] || 0;
            const active = intelHazardFilter.includes(h.id);
            return (
              <button
                key={h.id}
                type="button"
                onClick={() => (count > 0
                  ? setIntelHazardFilter((prev) => (prev.includes(h.id) ? prev.filter((x) => x !== h.id) : [...prev, h.id]))
                  : setZeroHazard(h))}
                data-testid={`intel-hazard-filter-${h.id}`}
                title={count === 0 ? 'No signals — click to see why and which APIs could help' : undefined}
                className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${active ? 'bg-slate-700 text-white border-slate-700' : count === 0 ? 'bg-white text-gray-400 border-dashed border-gray-300 hover:border-slate-400 hover:text-slate-500' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
              >
                {h.icon} {h.label} ({count})
              </button>
            );
          })}
        </div>
        {(() => {
          const q = intelQuery.trim().toLowerCase();
          let list = intelEvents;
          if (intelHazardFilter.length) list = list.filter((e) => e.hazards.some((h) => intelHazardFilter.includes(h)));
          if (q) list = list.filter((e) => `${e.title} ${e.description} ${e.connection?.name || ''}`.toLowerCase().includes(q));
          const shownIntel = showAllIntel ? list : list.slice(0, 6);
          if (list.length === 0) {
            return <p className="text-sm text-gray-400 text-center py-6">{intelEvents.length ? 'Nothing matches the search.' : 'No news or social signals right now.'}</p>;
          }
          return (
            <div className="space-y-2" data-testid="intel-list">
              {shownIntel.map((ev) => (
                <div key={`${ev.connection?.id}-${ev.external_id}`}
                  role="button"
                  onClick={() => onPreview(ev)}
                  className="flex items-start gap-2.5 rounded-lg border border-gray-100 bg-slate-50/40 hover:bg-slate-50 px-3 py-2 cursor-pointer"
                  data-testid={`intel-event-${ev.external_id}`}>
                  <Newspaper className="mt-1 h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-gray-800 leading-snug truncate" title={ev.title}>
                      {ev.internal && (
                        <span className="mr-1.5 inline-flex items-center rounded bg-slate-700 text-white text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 align-middle"
                          title={`Mentions ${countryName}`}>
                          {countryName}
                        </span>
                      )}
                      {ev.title}
                    </p>
                    <p className="text-[11px] text-gray-500">
                      {ev.connection?.name}
                      {ev.occurred_at && <span> · {new Date(ev.occurred_at).toLocaleString()}</span>}
                    </p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); onPreview(ev); }}
                    data-testid={`intel-dispatch-${ev.external_id}`}
                    className="flex items-center gap-1 rounded-md border border-slate-300 bg-white hover:bg-slate-100 text-slate-600 text-[10px] font-bold px-2 py-1.5 flex-shrink-0"
                    title="Preview & send to dispatch"
                  >
                    <Send className="h-3 w-3" /> Dispatch
                  </button>
                </div>
              ))}
              {list.length > 6 && (
                <button type="button" onClick={() => setShowAllIntel((v) => !v)} data-testid="intel-see-more"
                  className="w-full text-center text-[11px] font-bold text-slate-500 hover:text-slate-700 py-1">
                  {showAllIntel ? 'Show less' : `See more (${list.length - 6} more)`}
                </button>
              )}
            </div>
          );
        })()}
        {failedIntelSources.length > 0 && (
          <p className="mt-3 text-[10px] text-amber-600 flex items-center gap-1" data-testid="intel-failed-sources">
            <AlertTriangle className="h-3 w-3 flex-shrink-0" />
            {failedIntelSources.length} news/social feed{failedIntelSources.length === 1 ? '' : 's'} unavailable: {failedIntelSources.map((s) => s.name).join(', ')}
          </p>
        )}
      </div>
      )}
    </div>

    {/* ------------- Prediction — frequency & trend model (live) ------------- */}
    <PredictionSection collapsed={predictionCollapsed} onToggle={() => setPredictionCollapsed((v) => !v)} onPreview={onPreview} />

    {/* ---------------- IoT Watch — sensor trends & thresholds ---------------- */}
    {iotEnabled && <IotWatchSection />}
    </>
  );
};

/* ------------------------- Prediction section ------------------------- */
// A transparent statistical layer: the platform's OWN reported incidents,
// grouped by type+location, scored by frequency, acceleration and severity.
const PredictionSection = ({ collapsed, onToggle }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = async () => {
      try {
        const res = await CommanServices.getForesightPredictions();
        if (res?.status) setData(res.data);
      } catch (e) { /* shows on next tick */ }
    };
    load();
    const interval = setInterval(load, 120000);
    window.addEventListener('foresightRefresh', load);
    return () => { clearInterval(interval); window.removeEventListener('foresightRefresh', load); };
  }, []);

  const risks = data?.risks || [];
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mt-5" data-testid="prediction-section">
      <button type="button" onClick={onToggle} data-testid="prediction-collapse"
        className="w-full fs-sec-head px-5 py-3.5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="fs-sec-icon"><Radar className="h-4 w-4" /></div>
          <div className="text-left">
            <h3 className="text-sm font-bold leading-tight flex items-center gap-2">
              Prediction
              {risks.length > 0 && (
                <span className="text-[10px] font-bold fs-count-grey rounded-full px-2 py-0.5" data-testid="prediction-count">
                  {risks.length} predicted risk{risks.length === 1 ? '' : 's'}
                </span>
              )}
            </h3>
            <p className="text-[11px] fs-sec-sub">{data?.model || 'Frequency & trend model over your reported incidents'}</p>
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 transition-transform ${collapsed ? '' : 'rotate-180'}`} />
      </button>
      {!collapsed && (
        <div className="p-4" data-testid="prediction-body">
          {risks.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">
              Not enough recent incident history to predict anything — the model needs repeated real incidents to find patterns.
            </p>
          ) : (
            <div className="space-y-2">
              {risks.map((r) => (
                <div key={`${r.incident_type}-${r.location}`} className="rounded-lg border border-violet-100 bg-violet-50/40 px-3 py-2.5" data-testid={`prediction-risk-${r.incident_type}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-bold text-gray-800">{r.incident_type}</span>
                    <span className="text-[11px] text-gray-500">· {r.location}</span>
                    <span className={`ml-auto text-[11px] font-black ${r.score >= 60 ? 'text-rose-600' : r.score >= 35 ? 'text-orange-600' : 'text-violet-600'}`}>
                      {r.score}% risk
                    </span>
                  </div>
                  <div className="mt-1.5 h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
                    <div className={`h-full rounded-full ${r.score >= 60 ? 'bg-rose-500' : r.score >= 35 ? 'bg-orange-500' : 'bg-violet-500'}`} style={{ width: `${r.score}%` }} />
                  </div>
                  <p className="text-[11px] text-gray-600 mt-1.5">{r.rationale}</p>
                </div>
              ))}
              <p className="text-[10px] text-gray-400">
                Transparent statistics over {data?.incidents_analyzed ?? 0} recent real incidents — a decision aid, not a certainty.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* -------------------------- IoT Watch section -------------------------- */
// Live sensor watching: velocity (how fast a reading is moving), ETA to the
// configured threshold, proactive warnings when a breach is coming, and an
// automatic REAL incident the moment a threshold is crossed.
const IotWatchSection = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = async () => {
      try {
        const res = await CommanServices.getIotWatch();
        if (res?.status) setData(res.data);
      } catch (e) { /* shows on next tick */ }
    };
    load();
    const interval = setInterval(load, 60000);
    window.addEventListener('foresightRefresh', load);
    return () => { clearInterval(interval); window.removeEventListener('foresightRefresh', load); };
  }, []);

  const devices = data?.devices || [];
  const alerts = devices.filter((d) => d.status !== 'ok').length;
  const STATUS_BADGE = {
    ok: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    trending: 'bg-amber-50 text-amber-700 border-amber-200',
    breach: 'bg-rose-50 text-rose-700 border-rose-200',
  };
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mt-5 mb-6" data-testid="iot-watch-section">
      <button type="button" onClick={() => setCollapsed((v) => !v)} data-testid="iot-watch-collapse"
        className="w-full fs-sec-head px-5 py-3.5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="fs-sec-icon teal"><Activity className="h-4 w-4" /></div>
          <div className="text-left">
            <h3 className="text-sm font-bold leading-tight flex items-center gap-2">
              IoT Watch
              {alerts > 0 && (
                <span className="text-[10px] font-bold text-rose-100 bg-rose-500/60 border border-rose-300/40 rounded-full px-2 py-0.5" data-testid="iot-alert-count">
                  {alerts} alert{alerts === 1 ? '' : 's'}
                </span>
              )}
            </h3>
            <p className="text-[11px] fs-sec-sub">
              Trend tracking (velocity) · proactive warnings before thresholds are breached · breach = real incident, instantly
            </p>
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 transition-transform ${collapsed ? '' : 'rotate-180'}`} />
      </button>
      {!collapsed && (
        <div className="p-4" data-testid="iot-watch-body">
          {devices.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">
              No IoT devices connected yet — the watch engine is live and waiting. Once devices report readings,
              their velocity, ETA-to-threshold and breach status appear here automatically.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="iot-devices-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-gray-400 bg-gray-50/70 border-b">
                    <th className="text-left font-bold px-3 py-2">Sensor</th>
                    <th className="text-right font-bold px-3 py-2">Latest</th>
                    <th className="text-right font-bold px-3 py-2">Threshold</th>
                    <th className="text-right font-bold px-3 py-2">Velocity /h</th>
                    <th className="text-right font-bold px-3 py-2">ETA to breach</th>
                    <th className="text-left font-bold px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {devices.map((d) => (
                    <tr key={d.device.id} className="hover:bg-slate-50/60" data-testid={`iot-device-${d.device.id}`}>
                      <td className="px-3 py-2.5 font-semibold text-gray-900">{d.device.name || d.device.id}
                        <span className="ml-1.5 text-[10px] text-gray-400">{d.device.metric}</span></td>
                      <td className="px-3 py-2.5 text-right font-bold text-gray-700">{d.latest_value ?? '—'} {d.device.unit || ''}</td>
                      <td className="px-3 py-2.5 text-right text-gray-500">{d.device.threshold ?? '—'} {d.device.unit || ''}</td>
                      <td className={`px-3 py-2.5 text-right font-bold ${(d.velocity_per_hour || 0) > 0 ? 'text-amber-600' : 'text-gray-500'}`}>
                        {d.velocity_per_hour != null ? `${d.velocity_per_hour > 0 ? '+' : ''}${d.velocity_per_hour}` : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right font-bold text-gray-700">{d.eta_to_threshold_hours != null ? `~${d.eta_to_threshold_hours} h` : '—'}</td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_BADGE[d.status] || STATUS_BADGE.ok}`}>
                          {d.status === 'trending' ? '⚠ trending to breach' : d.status === 'breach' ? '🚨 breached' : '● ok'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* -------------------------------- Page --------------------------------- */
const ForeSight = () => {
  const navigate = useNavigate();
  const [queue, setQueue] = useState(null);
  const [queueError, setQueueError] = useState('');
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [refreshingQueue, setRefreshingQueue] = useState(false);
  const currentUser = useMemo(() => readStoredUser(), []);
  const apiConnectionsEnabled = isFeatureEnabled(currentUser, LICENSE_FEATURES.API_CONNECTIONS);

  const loadQueue = useCallback(async (refresh = false, silent = false) => {
    if (!silent) {
      if (refresh) setRefreshingQueue(true);
      else setLoadingQueue(true);
    }
    try {
      setQueueError('');
      if (refresh && apiConnectionsEnabled) {
        try {
          await CommanServices.pollExternalFeedsNow();
        } catch (error) {
          toast.error('Some connected sources could not be refreshed');
        }
      }
      const response = await CommanServices.getForesightDecisionQueue();
      const payload = response?.data || response;
      if (response?.status === false || !payload?.items) {
        const message = response?.message || 'The ForeSight service returned an invalid response.';
        setQueueError(message);
        if (!silent) toast.error(message);
        return;
      }
      setQueue(payload);
      setQueueError('');
      window.dispatchEvent(new Event('foresightRefresh'));
    } catch (error) {
      const message = error?.message || 'Could not reach the ForeSight service.';
      setQueueError(message);
      if (!silent) toast.error(message);
    } finally {
      if (!silent) {
        setLoadingQueue(false);
        setRefreshingQueue(false);
      }
    }
  }, [apiConnectionsEnabled]);

  useEffect(() => {
    loadQueue();
    const silentSync = () => loadQueue(false, true);
    const heartbeat = setInterval(silentSync, 60000);
    window.addEventListener('earlyWarningsChanged', silentSync);
    window.addEventListener('focus', silentSync);
    return () => {
      clearInterval(heartbeat);
      window.removeEventListener('earlyWarningsChanged', silentSync);
      window.removeEventListener('focus', silentSync);
    };
  }, [loadQueue]);

  const createScenario = async (item) => {
    const warningId = item?.warning_id;
    if (!warningId) {
      toast.error('This detection is missing its authoritative ForeSight record');
      return;
    }
    try {
      const response = await CommanServices.scenarioPrefillFromWarning({
        warning_id: warningId,
      });
      const prefill = (response?.data || response)?.prefill;
      if (response?.status === false || !prefill) {
        toast.error(response?.message || 'Could not prepare this detection for Scenario Builder');
        return;
      }
      navigate('/scenario-library', { state: { agentPrefill: prefill } });
    } catch (error) {
      toast.error(error?.message || 'Could not prepare this detection for Scenario Builder');
    }
  };

  return (
    <div className="h-screen flex flex-1 overflow-hidden">
      <div className="flex-1 p-5 overflow-y-auto fs-light">
        <ForesightDecisionQueue
          data={queue}
          error={queueError}
          loading={loadingQueue}
          refreshing={refreshingQueue}
          onRefresh={() => loadQueue(true)}
          onManage={apiConnectionsEnabled ? (hazard) => navigate(
            hazard?.id
              ? `/api-connections?hazard=${encodeURIComponent(hazard.id)}`
              : '/api-connections'
          ) : undefined}
          onAddConnection={apiConnectionsEnabled ? (hazard, geographicScope) => navigate(
            `/api-connections?hazard=${encodeURIComponent(hazard?.id || '')}` +
            `&geographic_scope=${encodeURIComponent(geographicScope || 'operations_area')}`
          ) : undefined}
          onScenario={createScenario}
        />
      </div>
    </div>
  );
};

export default ForeSight;
